<script setup>
import { computed, ref, useTemplateRef, onMounted, onBeforeUnmount } from 'vue';
import { useElementSize } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';

const { attachments, content, createdAt } = useMessageContext();
const { t } = useI18n();

const badgeTime = computed(() =>
  messageTimestamp(createdAt.value, 'd MMM, HH:mm')
);

const attachment = computed(() => attachments.value?.[0]);

// Provedores nativos encaminham localização como TEXTO (sem attachment
// file_type=location), num template que pode vir em qualquer idioma
// (waha/evolution/AstraCall não seguem um texto fixo). Por isso a extração
// abaixo se apoia só em assinaturas estruturais — URL de mapa (Google/Apple
// Maps, URI `geo:`), o emoji 📍 do cabeçalho e o padrão "Rótulo: valor" —
// nunca em palavras específicas de um idioma (ex.: "Endereço").
const rawText = computed(() => String(content?.value || ''));

const MAPS_QUERY_REGEX = /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/;
const GEO_URI_REGEX = /geo:(-?\d+\.?\d*),(-?\d+\.?\d*)/;

const coordinatesFromText = computed(() => {
  if (attachment.value) return null;

  const match =
    rawText.value.match(MAPS_QUERY_REGEX) || rawText.value.match(GEO_URI_REGEX);
  return match ? { lat: match[1], long: match[2] } : null;
});

// Linha de link: URL de verdade OU marcada com 🔗/🌐 — alguns provedores
// mandam a URL com as barras escapadas em entidade HTML (`https:&#x2F;&#x2F;`),
// então não dá pra confiar só no formato da URL.
const LINK_LINE_REGEX = /https?:\/\/\S+|geo:-?\d|🔗|🌐/;
// Rótulo genérico "algo: valor" (com ou sem `**` de markdown), captura o
// valor após o ":" onde quer que ele apareça na linha — cobre tanto
// "**Endereço:** ..." quanto uma linha prefixada com emoji (📍 **Endereço:**).
const LABELED_LINE_REGEX = /:\**\s*(.+)$/;

// Quando é um LOCAL nomeado (não a localização em tempo real), o texto traz
// nome + um campo "Rótulo: endereço" + um campo "Rótulo: site" (link à
// parte, não mostrado — WhatsApp não exibe site no card de localização) +
// o link do mapa. Extrai nome e endereço linha a linha pra não misturar os
// campos, sem depender do texto do rótulo em si (só da posição do ":").
const placeFromText = computed(() => {
  if (attachment.value) return { name: '', address: '' };

  let name = '';
  let address = '';
  rawText.value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .forEach(line => {
      if (LINK_LINE_REGEX.test(line)) return;

      const labeledMatch = line.match(LABELED_LINE_REGEX);
      if (labeledMatch) {
        address = labeledMatch[1].trim();
        return;
      }

      // Cabeçalho decorativo (ex.: "📍 Localização compartilhada"), sem
      // rótulo — não é o nome do local.
      if (line.startsWith('📍')) return;
      if (!name) name = line.replace(/\*\*/g, '').trim();
    });

  return { name, address };
});

const lat = computed(
  () => attachment.value?.coordinatesLat ?? coordinatesFromText.value?.lat
);
const long = computed(
  () => attachment.value?.coordinatesLong ?? coordinatesFromText.value?.long
);

const title = computed(
  () =>
    attachment.value?.fallbackTitle ??
    attachment.value?.fallback_title ??
    placeFromText.value.name
);

const address = computed(() => placeFromText.value.address);

const mapUrl = computed(
  () => `https://maps.google.com/?q=${lat.value},${long.value}`
);

// Chatwoot aplica a classe `dark` no <body> (themeHelper.js), não no <html>.
const isDark = ref(document.body.classList.contains('dark'));
let darkObserver;
onMounted(() => {
  darkObserver = new MutationObserver(() => {
    isDark.value = document.body.classList.contains('dark');
  });
  darkObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});
onBeforeUnmount(() => {
  darkObserver?.disconnect();
});

// O pino é FIXO no centro do card via CSS (sem cálculo nenhum). Quem se move
// é a imagem. O card (268 a ~320px de largura, variável com o texto da
// bolha — MEDIDO de verdade via ResizeObserver) é maior que um tile nativo
// (256x256), então 1 tile só não é suficiente pra cobrir tudo mantendo o
// ponto no centro: buscamos os tiles vizinhos necessários (até 2x2) pra
// garantir que o card fique todo coberto, sem esticar/borrar nenhum deles.
const mapContainer = useTemplateRef('mapContainer');
const MAP_HEIGHT = 120;
const mapContainerSize = useElementSize(
  mapContainer,
  { width: 268, height: MAP_HEIGHT },
  { box: 'border-box' }
);

const ZOOM = 15;
const TILE_SIZE = 256;
const TILE_SUBDOMAINS = ['a', 'b', 'c', 'd'];

const tileSrc = (tileX, tileY) => {
  const subdomain = TILE_SUBDOMAINS[(tileX + tileY) % TILE_SUBDOMAINS.length];
  return isDark.value
    ? `https://${subdomain}.basemaps.cartocdn.com/dark_all/${ZOOM}/${tileX}/${tileY}.png`
    : `https://tile.openstreetmap.org/${ZOOM}/${tileX}/${tileY}.png`;
};

const mapView = computed(() => {
  const mapWidth = mapContainerSize.width.value || 268;
  const mapHeight = mapContainerSize.height.value || MAP_HEIGHT;

  const latValue = Number(lat.value);
  const longValue = Number(long.value);
  const worldSize = TILE_SIZE * 2 ** ZOOM;
  const worldX = ((longValue + 180) / 360) * worldSize;
  const sinLat = Math.sin((latValue * Math.PI) / 180);
  const worldY =
    (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * worldSize;

  // Canto superior-esquerdo (em px de mundo) da janela mapWidth x mapHeight
  // centrada no ponto — é essa janela que o card precisa exibir por inteiro.
  const originX = worldX - mapWidth / 2;
  const originY = worldY - mapHeight / 2;

  const minTileX = Math.floor(originX / TILE_SIZE);
  const minTileY = Math.floor(originY / TILE_SIZE);
  const maxTileX = Math.floor((originX + mapWidth) / TILE_SIZE);
  const maxTileY = Math.floor((originY + mapHeight) / TILE_SIZE);

  const tiles = [];
  for (let tileY = minTileY; tileY <= maxTileY; tileY += 1) {
    for (let tileX = minTileX; tileX <= maxTileX; tileX += 1) {
      tiles.push({
        key: `${tileX}-${tileY}`,
        src: tileSrc(tileX, tileY),
        left: `${tileX * TILE_SIZE - originX}px`,
        top: `${tileY * TILE_SIZE - originY}px`,
      });
    }
  }

  return { tiles };
});

// Fallback pra quando a Clipboard API não está disponível/permitida (ex.:
// contexto não seguro ou Permissions-Policy bloqueando `clipboard-write`) —
// sem isso, `navigator.clipboard` pode nem existir e o clique não faz nada,
// silenciosamente.
const legacyCopy = text => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const success = document.execCommand('copy');
  document.body.removeChild(textarea);
  return success;
};

const copied = ref(false);
const copyLocationUrl = async () => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(mapUrl.value);
    } else if (!legacyCopy(mapUrl.value)) {
      throw new Error('Clipboard copy failed');
    }
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch (e) {
    copied.value = false;
    useAlert(t('COMPONENTS.LOCATION_BUBBLE.COPY_FAILED'));
  }
};
</script>

<template>
  <BaseBubble
    class="!p-0 overflow-hidden !max-w-[320px] min-w-[268px]"
    data-bubble-name="location"
    hide-meta
  >
    <a
      ref="mapContainer"
      :href="mapUrl"
      rel="noreferrer noopener nofollow"
      target="_blank"
      class="relative block mt-1 mx-px overflow-hidden bg-n-solid-2 rounded-xl w-[calc(100%-2px)] h-[120px]"
    >
      <img
        v-for="tile in mapView.tiles"
        :key="tile.key"
        :src="tile.src"
        alt=""
        class="absolute w-[256px] h-[256px]"
        :style="{ left: tile.left, top: tile.top }"
      />
      <span
        class="absolute z-10 left-1/2 top-1/2 text-2xl -translate-x-1/2 -translate-y-full i-ph-map-pin-fill text-n-ruby-9 drop-shadow"
      />
      <span
        class="absolute z-10 bottom-1.5 right-1.5 px-1.5 py-0.5 rounded-full text-[0.625rem] font-bold text-n-slate-11 bg-n-solid-1/90 shadow-sm"
      >
        {{ badgeTime }}
      </span>
    </a>

    <div v-if="title || address" class="p-3 space-y-1">
      <div v-if="title" class="text-sm font-medium text-n-slate-12">
        {{ title }}
      </div>
      <div v-if="address" class="text-xs text-n-slate-11">
        {{ address }}
      </div>
    </div>

    <div class="flex border-t border-n-weak divide-x divide-n-weak">
      <a
        :href="mapUrl"
        rel="noreferrer noopener nofollow"
        target="_blank"
        class="flex items-center justify-center flex-1 gap-2 py-2.5 text-sm font-medium text-n-blue-11 hover:bg-n-alpha-1"
      >
        <span class="size-4 i-ph-arrow-square-out" />
        {{ t('COMPONENTS.LOCATION_BUBBLE.SEE_ON_MAP') }}
      </a>
      <button
        type="button"
        :title="t('COMPONENTS.LOCATION_BUBBLE.COPY_COORDINATES')"
        class="flex items-center justify-center flex-1 py-2.5 text-n-blue-11 hover:bg-n-alpha-1"
        @click="copyLocationUrl"
      >
        <span
          :class="copied ? 'i-ph-check-bold' : 'i-ph-copy'"
          class="size-4"
        />
      </button>
    </div>
  </BaseBubble>
</template>
